/**
 * SystemProduct — the platform's canonical, size-agnostic product line
 * (T12.1.8). Machine-built from the retailer offer stream; the household never
 * sees "SystemProduct" (the UI says "products"). This file models only the
 * read-side surface the picker consumes (T12.1.8.2):
 *   - `searchSystemProducts` results (free-text + category/brand/available filters),
 *   - the "who offers it" grouped read,
 *   - the household's per-HouseholdProduct SystemProduct preference rows.
 *
 * Wire shape is snake_case (mirrors the Postgres column naming and the backend
 * serializers), same convention as `HouseholdProduct` / `RetailerOffer`.
 */

import { z } from 'zod';
import { retailerOfferWithDetailsSchema } from './RetailerOffer';

// ---------------------------------------------------------------------------
// Preference — same closed set as the brand preference: preferred / substitute /
// exclude ("Don't suggest", a soft downweight weighed by T12.5, never a hard filter)
// ---------------------------------------------------------------------------

export const systemProductPreferenceSchema = z.union([
  z.literal('preferred'),
  z.literal('substitute'),
  z.literal('exclude'),
]);
export type SystemProductPreference = z.infer<typeof systemProductPreferenceSchema>;

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

// One matched SystemProduct row in a search result. `available_at_my_stores`
// is the actionable facet — true when the line has ≥1 active offer at a store
// the household selected; available rows rank first.
export const systemProductSearchResultSchema = z.object({
  id: z.string(),
  name: z.string(),
  image_url: z.string().nullable().optional(),
  brand_id: z.string().nullable().optional(),
  normalized_brand_name: z.string().nullable().optional(),
  brand_name: z.string().nullable().optional(),
  category_ids: z.array(z.string()),
  available_at_my_stores: z.boolean(),
});
export type SystemProductSearchResult = z.infer<typeof systemProductSearchResultSchema>;

export const systemProductSearchResponseSchema = z.object({
  items: z.array(systemProductSearchResultSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});
export type SystemProductSearchResponse = z.infer<typeof systemProductSearchResponseSchema>;

// Query params the picker passes to the search endpoint. `query` is the
// free-text term; the rest are pre-filters. `category_ids` are parent-or-leaf
// tags (the backend walks the seed_categories subtree). `available_only`
// hard-restricts to available-at-my-stores rows; absent it only re-ranks.
export const systemProductSearchParamsSchema = z.object({
  query: z.string().optional(),
  category_ids: z.array(z.string()).optional(),
  brand_id: z.string().optional(),
  available_only: z.boolean().optional(),
  limit: z.number().int().positive().optional(),
  offset: z.number().int().nonnegative().optional(),
});
export type SystemProductSearchParams = z.infer<typeof systemProductSearchParamsSchema>;

// ---------------------------------------------------------------------------
// "Who offers it" — offers grouped by the household's selected stores
// ---------------------------------------------------------------------------

export const systemProduct_StoreLocationOffersSchema = z.object({
  storelocation_id: z.string(),
  store_name: z.string(),
  offers: z.array(retailerOfferWithDetailsSchema),
});
export type SystemProduct_StoreLocationOffers = z.infer<typeof systemProduct_StoreLocationOffersSchema>;

export const systemProductOffersResponseSchema = z.object({
  systemproduct_id: z.string(),
  stores: z.array(systemProduct_StoreLocationOffersSchema),
});
export type SystemProductOffersResponse = z.infer<typeof systemProductOffersResponseSchema>;
